import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Button } from 'components/ui/Button/Button'
import { Flex, H1WithBack, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText } from 'styling/Typography'
import { getLiveTasks } from 'features/operations/opsLiveTasksSlice'
import { getDeptOpsProfiles } from 'features/operations/deptOpsProfilesSlice'
import { CORE_STATE_META, SOURCE_META, taskCoreState } from 'pages/Operations/Shared/opsControl'
import { opsDepartments } from 'pages/Operations/Shared/opsDepartments'
import { OpsCoreState, OpsDepartmentKey, OpsTaskSource } from 'types/operationsControl.interface'
import {
  IDepartmentSourceFilterOption,
  IDepartmentStatusFilterOption,
  IDepartmentTabOption,
} from 'types/departmentBoard.interface'
import { TaskQueueCard } from './TaskQueueCard'
import { TeamPanel } from './TeamPanel'
import { DepartmentContextPanels } from './ContextPanels'
import { AddTaskModal } from '../Overview/Tasks/AddTaskModal'
import { BoardColumns, EmptyWrap, FilterBar, FilterGroup, FilterLabel, QueueColumn, SideColumn } from './DepartmentBoard.styled'

const STATUS_FILTERS: IDepartmentStatusFilterOption[] = [
  { key: 'ALL', label: 'All' },
  { key: 'DRAFT', label: 'Queued' },
  { key: 'ACTIVE', label: CORE_STATE_META.ACTIVE.label },
  { key: 'PAUSED', label: CORE_STATE_META.PAUSED.label },
  { key: 'BLOCKED', label: CORE_STATE_META.BLOCKED.label },
  { key: 'COMPLETED', label: CORE_STATE_META.COMPLETED.label },
]

const SOURCE_FILTERS: IDepartmentSourceFilterOption[] = [
  { key: 'ALL', label: 'All' },
  { key: 'PLAN', label: SOURCE_META.PLAN.label },
  { key: 'SHIFT_PROGRAM', label: SOURCE_META.SHIFT_PROGRAM.label },
  { key: 'TRIGGER', label: SOURCE_META.TRIGGER.label },
  { key: 'MANUAL', label: SOURCE_META.MANUAL.label },
  { key: 'REQUEST', label: SOURCE_META.REQUEST.label },
]

export const DepartmentBoard = () => {
  const dispatch = useDispatch<AppDispatch>()
  const profiles = useSelector((s: RootState) => s.deptOpsProfiles.profiles)
  const apiTasks = useSelector((s: RootState) => s.opsLiveTasks.tasks)
  const departmentTabs: IDepartmentTabOption[] = useMemo(
    () => opsDepartments(profiles).map((d) => ({ key: d.key, label: d.name })),
    [profiles],
  )
  const [department, setDepartment] = useState<OpsDepartmentKey>('')
  const [statusFilter, setStatusFilter] = useState<'ALL' | OpsCoreState>('ALL')
  const [sourceFilter, setSourceFilter] = useState<'ALL' | OpsTaskSource>('ALL')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [taskModalOpen, setTaskModalOpen] = useState(false)

  useEffect(() => {
    dispatch(getDeptOpsProfiles())
    dispatch(getLiveTasks({}))
  }, [dispatch])

  useEffect(() => {
    if (!department && departmentTabs.length) setDepartment(departmentTabs[0].key)
  }, [department, departmentTabs])

  const liveTasks = apiTasks
  const departmentLabel = departmentTabs.find((d) => d.key === department)?.label ?? ''

  const tasks = liveTasks.filter(
    (t) =>
      t.department === department &&
      (statusFilter === 'ALL' || taskCoreState(t.taskStatus) === statusFilter) &&
      (sourceFilter === 'ALL' || t.source === sourceFilter),
  )

  const selectDepartment = (key: OpsDepartmentKey) => {
    setDepartment(key)
    setExpandedId(null)
  }

  if (!departmentTabs.length) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <H1WithBack title='Department board' arrow />
          <NoData text='Departments will appear here once department profiles are loaded.' />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <H1WithBack title='Department board' arrow />
        <Flex mt='16'>
          <TabsContainer>
            {departmentTabs.map((d) => (
              <Tabs key={d.key} active={department === d.key} onClick={() => selectDepartment(d.key)}>
                {d.label}
              </Tabs>
            ))}
          </TabsContainer>
        </Flex>
        <FilterBar>
          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            <TabsContainer>
              {STATUS_FILTERS.map((f) => (
                <Tabs key={f.key} active={statusFilter === f.key} onClick={() => setStatusFilter(f.key)}>
                  {f.label}
                </Tabs>
              ))}
            </TabsContainer>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Source</FilterLabel>
            <TabsContainer>
              {SOURCE_FILTERS.map((f) => (
                <Tabs key={f.key} active={sourceFilter === f.key} onClick={() => setSourceFilter(f.key)}>
                  {f.label}
                </Tabs>
              ))}
            </TabsContainer>
          </FilterGroup>
        </FilterBar>
        <BoardColumns>
          <QueueColumn>
            {tasks.length ? (
              tasks.map((t) => (
                <TaskQueueCard
                  key={t.id}
                  task={t}
                  expanded={expandedId === t.id}
                  onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)}
                />
              ))
            ) : (
              <EmptyWrap>
                <BodyText grey>No tasks for {departmentLabel} this shift.</BodyText>
                <Button variant='primary' onClick={() => setTaskModalOpen(true)}>
                  Create manual task
                </Button>
              </EmptyWrap>
            )}
          </QueueColumn>
          <SideColumn>
            <TeamPanel workers={[]} />
            <DepartmentContextPanels department={department} />
          </SideColumn>
        </BoardColumns>
        {taskModalOpen && (
          <AddTaskModal
            closeModal={() => setTaskModalOpen(false)}
            onCreated={() => dispatch(getLiveTasks({}))}
          />
        )}
      </PageWrapper>
    </>
  )
}
